import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

interface TabsProps {
  tabs?: { label: string; content: React.ReactNode }[];
  activeTab?: number;
  isContactPage?: boolean;
  isCompanyPage?: boolean;
  showEmailTab?: boolean;
  isProposalPage?: boolean;
  isContractPage?: boolean;
  showProductsServicesTab?: boolean;
  showApprovalFlowTab?: boolean;
  hasApprovalAction?: boolean;
  showChatTab?: boolean;
}

const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, isContactPage, isCompanyPage, showEmailTab = true, isProposalPage, isContractPage, showProductsServicesTab, showApprovalFlowTab, hasApprovalAction, showChatTab }) => {
  const scrollRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div className="tabs">
      <div className="tabs-scroll-wrapper">
        {canScrollLeft && (
          <button
            type="button"
            className="tabs-scroll-btn tabs-scroll-btn-left"
            onClick={() => scroll("left")}
          >
            <i className="ti ti-chevron-left" />
          </button>
        )}
        {canScrollLeft && <div className="tabs-fade tabs-fade-left" />}
        <ul
          className="nav nav-tabs nav-tabs-bottom"
          role="tablist"
          ref={scrollRef}
        >
          <li className="nav-item" role="presentation">
            <Link
              to="#"
              data-bs-toggle="tab"
              data-bs-target="#activities"
              className="nav-link active"
            >
              <i className="ti ti-alarm-minus me-1" />
              Activities
            </Link>
          </li>
          <li className="nav-item" role="presentation">
            <Link
              to="#"
              data-bs-toggle="tab"
              data-bs-target="#notes"
              className="nav-link"
            >
              <i className="ti ti-notes me-1" />
              Notes
            </Link>
          </li>
          {(isProposalPage || isContractPage) && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#attachments"
                className="nav-link"
              >
                <i className="ti ti-paperclip me-1" />
                Attachments
              </Link>
            </li>
          )}
          <li className="nav-item" role="presentation">
            <Link
              to="#"
              data-bs-toggle="tab"
              data-bs-target="#files"
              className="nav-link"
            >
              <i className="fa-regular fa-file me-1" />
              Files
            </Link>
          </li>
          {isCompanyPage && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#related-files"
                className="nav-link"
              >
                <i className="fa-regular fa-file-lines me-1" />
                Linked Files
              </Link>
            </li>
          )}
          {showProductsServicesTab && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#products-services"
                className="nav-link"
              >
                <i className="fa-solid fa-cart-shopping me-1" />
                Products & Services
              </Link>
            </li>
          )}
          <li className="nav-item" role="presentation">
            <Link
              to="#"
              data-bs-toggle="tab"
              data-bs-target="#tasks"
              className="nav-link"
            >
              <i className="fa-solid fa-list-check me-1" />
              Tasks
            </Link>
          </li>
          {showApprovalFlowTab && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#approval-flow"
                className="nav-link"
              >
                <i className="ti ti-git-branch me-1" />
                Approval Flow
                {hasApprovalAction && (
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#f59e0b",
                      marginLeft: "6px",
                      verticalAlign: "middle",
                      flexShrink: 0,
                    }}
                  />
                )}
              </Link>
            </li>
          )}
          {isCompanyPage && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#contacts"
                className="nav-link"
              >
                <i className="ti ti-users me-1" />
                Contacts
              </Link>
            </li>
          )}
          {isContactPage && (
            <li
              className="nav-item"
              role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#stages"
                className="nav-link"
              >
                <i className="ti ti-activity me-1" />
                Stages
              </Link>
            </li>
          )}
          {showEmailTab && (
            <li
              className="nav-item "
              role="presentation"
            >
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#email"
                className="nav-link"
              >
                <i className="ti ti-mail-check me-1" />
                Email
              </Link>
            </li>
          )}
          {(isContactPage || showChatTab) && (
            <li className="nav-item" role="presentation">
              <Link
                to="#"
                data-bs-toggle="tab"
                data-bs-target="#chat"
                className="nav-link"
              >
                <i className="ti ti-message me-1" />
                Chat
              </Link>
            </li>
          )}
        </ul>
        {canScrollRight && <div className="tabs-fade tabs-fade-right" />}
        {canScrollRight && (
          <button
            type="button"
            className="tabs-scroll-btn tabs-scroll-btn-right"
            onClick={() => scroll("right")}
          >
            <i className="ti ti-chevron-right" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Tabs;